import { z } from 'zod';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { blockSchema } from './localAreaExpert.js';
import { socialMetadataSchema } from './localSocial.js';
import { sectionCtaSchema } from './localHomepage.js';

export const localBusinessCategories = ['Food & Drink', 'Home & Garden', 'Health & Wellness', 'Professional Services', 'Retail', 'Education', 'Leisure', 'Other'] as const;
const phone = z.string().trim().max(40).refine((value) => !value || Boolean(parsePhoneNumberFromString(value, 'ZA')?.isValid()), 'Enter a valid telephone number').transform((value) => value ? parsePhoneNumberFromString(value, 'ZA')!.number : '');
export const localBusinessDataSchema = z.object({
  name: z.string().trim().min(1, 'Business name is required').max(160),
  category: z.enum(localBusinessCategories),
  shortDescription: z.string().trim().max(500),
  description: z.string().trim().max(5000),
  blocks: z.array(blockSchema).max(100),
  logoMediaId: z.string().uuid().nullable(),
  photoMediaIds: z.array(z.string().uuid()).max(12).refine((ids) => new Set(ids).size === ids.length, 'Duplicate photographs'),
  street: z.string().trim().max(300), suburb: z.string().trim().max(120), city: z.string().trim().max(120), province: z.string().trim().max(120),
  website: z.string().trim().max(2000).refine((value) => {
    if (!value) return true;
    try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password && !url.port && !/^(localhost|127\.|0\.|10\.|192\.168\.|169\.254\.|\[)/i.test(url.hostname); } catch { return false; }
  }, 'Enter a public HTTPS website address'),
  whatsapp: phone, telephone: phone,
  email: z.union([z.literal(''), z.string().trim().email().max(254)]),
  verifiedLocation: z.null(),
  cta: sectionCtaSchema.optional(),
  social: socialMetadataSchema.optional(),
}).strict();
export type LocalBusinessData = z.infer<typeof localBusinessDataSchema>;
export const localBusinessStatuses = ['draft', 'published', 'unpublished'] as const;
export const localBusinessRecordSchema = z.object({ id: z.string().uuid(), status: z.enum(localBusinessStatuses), version: z.number().int().positive(), updatedAt: z.string().datetime(), data: localBusinessDataSchema }).strict();
export type LocalBusinessRecord = z.infer<typeof localBusinessRecordSchema>;
// Local-only simulation: Businesses have no scheduling, so a published Business is live until it is unpublished.
export const localBusinessLifecycleRequestSchema = z.object({ action: z.enum(['publish_now', 'unpublish', 'republish']), expectedVersion: z.number().int().positive() }).strict();
export type LocalBusinessLifecycleRequest = z.infer<typeof localBusinessLifecycleRequestSchema>;
export function localBusinessContactActions(data: Pick<LocalBusinessData, 'website' | 'whatsapp' | 'telephone' | 'email'>) {
  return [
    ...(data.website ? [{ label: 'View Website', href: data.website }] : []),
    ...(data.whatsapp ? [{ label: 'WhatsApp', href: `https://wa.me/${data.whatsapp.replace(/\D/g, '')}` }] : []),
    ...(data.telephone ? [{ label: 'Call', href: `tel:${data.telephone}` }] : []),
    ...(data.email ? [{ label: 'Email', href: `mailto:${data.email}` }] : []),
  ];
}